import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { myFetch } from '@/commons/fetch';
import { API_URL } from '@/commons/constants';

// Types
export interface UploadedFile {
  filename: string;
  originalName: string;
  size: number;
  mimetype: string;
  path: string;
  uploadedAt?: string;
}

interface UploadsState {
  uploads: UploadedFile[];
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;
  hasFetched: boolean;
}

const initialState: UploadsState = {
  uploads: [],
  isLoading: false,
  isUploading: false,
  error: null,
  hasFetched: false,
};

// Async Thunks
export const fetchUploads = createAsyncThunk(
  'uploads/fetchUploads',
  async (_, { rejectWithValue }) => {
    try {
      const response = await myFetch('/users/uploads', {
        method: 'GET',
      });
      return response.data || [];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch uploads');
    }
  }
);

export const uploadFile = createAsyncThunk(
  'uploads/uploadFile',
  async (file: File, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      // For file uploads, we need to use fetch directly since myFetch sets Content-Type: application/json
      // and we need the browser to set multipart/form-data with boundary
      const token = localStorage.getItem('userToken');
      const response = await fetch(`${API_URL}/api/v6/users/uploads`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          // Don't set Content-Type, let browser set it with boundary
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: response.statusText,
        }));
        throw new Error(
          errorData.message || errorData.error || 'Upload failed'
        );
      }

      const result = await response.json();
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to upload file');
    }
  }
);

export const deleteUpload = createAsyncThunk(
  'uploads/deleteUpload',
  async (filename: string, { rejectWithValue }) => {
    try {
      await myFetch(`/users/uploads/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
      });
      return filename;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete file');
    }
  }
);

// Slice
const uploadsSlice = createSlice({
  name: 'uploads',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearUploads: (state) => {
      state.uploads = [];
      state.error = null;
      state.hasFetched = false;
    },
    setUploading: (state, action) => {
      state.isUploading = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Uploads
    builder
      .addCase(fetchUploads.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUploads.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploads = action.payload;
        state.hasFetched = true;
      })
      .addCase(fetchUploads.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.hasFetched = true;
      });

    // Upload File
    builder
      .addCase(uploadFile.pending, (state) => {
        state.isUploading = true;
        state.error = null;
      })
      .addCase(uploadFile.fulfilled, (state) => {
        state.isUploading = false;
        // Note: We'll refetch uploads after successful upload
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload as string;
      });

    // Delete Upload
    builder
      .addCase(deleteUpload.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUpload.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploads = state.uploads.filter(
          (upload) => upload.filename !== action.payload
        );
      })
      .addCase(deleteUpload.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearUploads, setUploading } = uploadsSlice.actions;
export default uploadsSlice.reducer;
